import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../config/api';
import { Tour } from '../types';
import TourLanguageDialog from '../components/TourLanguageDialog';

const Tours: React.FC = () => {
  const navigate = useNavigate();
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);
  const [translationTour, setTranslationTour] = useState<Tour | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.tours);
      setTours(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch tours');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedTour(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const handleEdit = (tour: Tour) => {
    setSelectedTour(tour);
    setFormData({ name: tour.name, description: tour.description });
    setDialogOpen(true);
  };

  const handleDelete = (tour: Tour) => {
    setSelectedTour(tour);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedTour) {
        // Update existing tour
        await api.put(endpoints.tour(selectedTour.id), formData);
        setSuccess('Tour updated successfully');
      } else {
        // Create new tour
        await api.post(endpoints.tours, formData);
        setSuccess('Tour created successfully');
      }
      setDialogOpen(false);
      fetchTours();
    } catch (err) {
      setError('Failed to save tour');
      console.error(err);
    }
  };

  const confirmDelete = async () => {
    if (selectedTour) {
      try {
        await api.delete(endpoints.tour(selectedTour.id));
        setSuccess('Tour deleted successfully');
        setDeleteDialogOpen(false);
        fetchTours();
      } catch (err) {
        setError('Failed to delete tour');
        console.error(err);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading tours...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Tours</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Tour
        </Button>
      </Box>

      {tours.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" align="center">
              No tours found. Create your first tour to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {tours.map((tour) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={tour.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {tour.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {tour.description || 'No description'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Stops: {tour.tourStops?.length || 0}
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/tours/${tour.id}`)}
                    title="View Details"
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(tour)}
                    title="Edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(tour)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                  <Tooltip title="Crea tour in un’altra lingua">
                    <IconButton
                      size="small"
                      aria-label={`Crea ${tour.name} in un’altra lingua`}
                      onClick={() => setTranslationTour(tour)}
                    >
                      <TranslateIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {translationTour && (
        <TourLanguageDialog
          key={translationTour.id}
          tour={translationTour}
          onClose={() => setTranslationTour(null)}
          onCreated={(tour) => {
            setTours(current => [tour, ...current]);
            setTranslationTour(null);
            setSuccess(`Tour "${tour.name}" creato con successo`);
          }}
        />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTour ? 'Edit Tour' : 'Create Tour'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tour Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name}>
            {selectedTour ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tour "{selectedTour?.name}"?
            This will also delete all associated tour stops.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tours;
